import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScrollToTop } from '../../hooks/useScrollToTop';
import { IoChatbubbleEllipsesOutline } from 'react-icons/io5';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import './LandlordDashboard.css';
import Overview from './components/Overview';
import ManageRentals from './components/ManageRentals';
import Agents from './components/Agents';
import RentStatus from './components/RentStatus';
import Analytics from './components/Analytics';
import Notifications from '../shared/Notifications/Notifications';
import Profile from '../Profile/Profile';
import LandlordUnitRequests from './components/LandlordUnitRequests';
import RentEvidenceView from './components/RentEvidenceView';
import WaterUsageView from './components/WaterUsageView';
import PropertyMaintenanceView from './components/PropertyMaintenanceView';
import MoveOutRequests from './components/MoveOutRequests';
import Messages from '../shared/Messages/Messages';
import TenantScore from './components/TenantScore';
import PropertyRatings from '../shared/PropertyRatings/PropertyRatings';



const LandlordDashboard = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [navigationState, setNavigationState] = useState({});
  const navigate = useNavigate();

  useScrollToTop(activeSection);

  useEffect(() => {
    const storedSubscriptionState = localStorage.getItem('tenantscore_subscription_state');
    if (storedSubscriptionState === 'required') {
      navigate('/subscription-plans', { replace: true, state: { returnTo: '/landlord' } });
    }
  }, [navigate]);

  const handleNavigate = (section, state = {}) => {
    setActiveSection(section);
    setNavigationState(state);
  };

  const sidebarItems = [
    { id: 'overview', label: 'Dashboard', icon: '🏠', section: 'MANAGEMENT' },
    { id: 'manage-rentals', label: 'Manage Rentals', icon: '🏡', section: 'MANAGEMENT' },
    { id: 'unit-requests', label: 'Unit requests', icon: '📬', section: 'MANAGEMENT' },
    { id: 'move-out-requests', label: 'Move-out requests', icon: '🚚', section: 'MANAGEMENT' },
    { id: 'maintenance', label: 'Maintenance', icon: '🔧', section: 'MANAGEMENT' },
    { id: 'rent-evidence', label: 'Rent evidence', icon: '📎', section: 'FINANCE' },
    { id: 'water-usage', label: 'Water usage', icon: '💧', section: 'FINANCE' },
    { id: 'rent-status', label: 'Rent Status', icon: '💰', section: 'FINANCE' },
    { id: 'tenant-score', label: 'Tenant Score', icon: '⭐', section: 'TRUST & SECURITY' },
    { id: 'agents', label: 'Agents', icon: '👤', section: 'MANAGEMENT' },
    { id: 'messages', label: 'Messages', icon: <IoChatbubbleEllipsesOutline />, section: 'COMMUNICATION' },
    { id: 'analytics', label: 'Analytics', icon: '📊', section: 'TRUST & SECURITY' },
    { id: 'property-ratings', label: 'Property Ratings', icon: '⭐', section: 'TRUST & SECURITY' },
    { id: 'profile', label: 'My Profile', icon: '👤', section: 'SYSTEM' },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return <Overview onNavigate={setActiveSection} />;
      case 'manage-rentals':
        return <ManageRentals />;
      case 'unit-requests':
        return <LandlordUnitRequests navigationState={navigationState} onNavigate={handleNavigate} />;
      case 'maintenance':
        return <PropertyMaintenanceView />;
      case 'move-out-requests':
        return <MoveOutRequests navigationState={navigationState} />;
      case 'rent-evidence':
        return React.createElement(RentEvidenceView);
      case 'water-usage':
        return <WaterUsageView />;
      case 'rent-status':
        return <RentStatus />;
      case 'tenant-score':
        return <TenantScore variant="landlord" navigationState={navigationState} />;
      case 'agents':
        return <Agents />;
      case 'analytics':
        return <Analytics />;
      case 'property-ratings':
        return <PropertyRatings dashboardScope="LANDLORD" />;
      case 'profile':
        return <Profile />;
      case 'messages':
        return (
          <Messages
            userRole="Landlord"
            threadId={navigationState?.threadId}
            threadData={navigationState?.threadData}
          />
        );
      case 'notifications':
        return <Notifications onNavigate={handleNavigate} userRole="Landlord" />;
      default:
        return <Overview onNavigate={setActiveSection} />;
    }
  };

  return (
    <div className="dashboard">
      <Sidebar items={sidebarItems} activeItem={activeSection} onItemClick={handleNavigate} variant="landlord" />
      <div className="dashboard-content">
        <Header
          userRole="Landlord"
          activeSection={activeSection}
          onNavClick={handleNavigate}
        />
        <main className="dashboard-main">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default LandlordDashboard;
